<template>
  <div class="max-w-2xl mx-auto px-4 py-6 sm:px-6 lg:px-8 space-y-6">
    <div class="flex items-center justify-between">
      <button @click="goBack" class="btn btn-secondary !p-2 !rounded-full">
        <span class="material-symbols-outlined">arrow_back</span>
      </button>
      <h1 class="text-xl font-bold text-slate-800 dark:text-slate-100">
        {{ isEditing ? 'Editar Meta' : 'Nova Meta' }}
      </h1>
      <button @click="handleSubmit" :disabled="isSubmitting" class="btn btn-primary min-w-[80px]">
        <span v-if="isSubmitting" class="material-symbols-outlined animate-spin text-lg">progress_activity</span>
        <span v-else>Salvar</span>
      </button>
    </div>

    <div class="panel p-5 sm:p-6">
      <form @submit.prevent="handleSubmit" class="space-y-6">
        <div class="space-y-2">
          <label for="title" class="label">Título da Meta</label>
          <input id="title" v-model="meta.title" type="text" required placeholder="Ex: Viagem para a Europa"
            class="input" />
        </div>

        <div class="space-y-2" ref="iconDropdownContainer">
          <label class="label">Ícone</label>
          <button type="button" @click="isIconDropdownOpen = !isIconDropdownOpen"
            class="input flex items-center justify-between text-left">
            <span class="flex items-center gap-2">
              <span class="material-symbols-outlined text-base text-slate-600 dark:text-slate-300">
                {{ meta.icon || 'savings' }}
              </span>
              <span class="text-slate-700 dark:text-slate-200">{{ selectedIconLabel }}</span>
            </span>
            <span class="material-symbols-outlined text-slate-400">unfold_more</span>
          </button>

          <transition name="dropdown">
            <div v-if="isIconDropdownOpen"
              class="absolute z-10 mt-1 p-2 w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-lg max-h-60 overflow-y-auto">
              <div class="grid grid-cols-6 gap-1">
                <button v-for="icone in iconesDisponiveis" :key="icone.value" type="button" :title="icone.label"
                  @click="selectIcon(icone.value)" class="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
                  :class="{ 'bg-brand-500/10 dark:bg-slate-900 ring-1 ring-brand-200 dark:ring-slate-600': meta.icon === icone.value }">
                  <span class="material-symbols-outlined text-slate-700 dark:text-slate-300">{{ icone.value }}</span>
                </button>
              </div>
            </div>
          </transition>
        </div>

        <div v-if="meta.icon === 'payments'" class="pt-4 border-t border-slate-200 dark:border-slate-800">
          <div v-if="isFetchingDividas" class="flex items-center gap-2 text-sm text-slate-500">
            <span class="material-symbols-outlined animate-spin">progress_activity</span>
            Calculando dívidas de {{ nomeMesAtual }}...
          </div>
          <div v-else class="rounded-xl bg-slate-50 dark:bg-slate-800/50 p-4">
            <p class="text-sm text-slate-600 dark:text-slate-400">
              Valor alvo definido com a soma das dívidas pendentes (fixas + cartão) para <b>{{ nomeMesAtual }}</b>.
            </p>
            <p class="mt-2 text-2xl font-bold text-brand-600 dark:text-brand-400">
              {{ formatCurrency(meta.targetAmount) }}
            </p>
          </div>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-6">
          <div class="space-y-2">
            <label for="currentAmount" class="label">Valor Atual (R$)</label>
            <input id="currentAmount" v-model.number="meta.currentAmount" type="number" step="0.01" required
              placeholder="0,00" class="input" />
          </div>
          <div class="space-y-2">
            <label for="targetAmount" class="label">Valor Alvo (R$)</label>
            <input id="targetAmount" v-model.number="meta.targetAmount" type="number" step="0.01" required
              placeholder="1.000,00" class="input" :readonly="meta.icon === 'payments'" :class="{'bg-slate-100 dark:bg-slate-700/50 cursor-not-allowed': meta.icon === 'payments'}" />
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch, onUnmounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import axios from 'axios';

const router = useRouter();
const route = useRoute();

const isEditing = computed(() => !!route.params.id);
const isSubmitting = ref(false);

const meta = ref({ id: null, title: '', icon: 'savings', currentAmount: 0, targetAmount: 1000 });
const apiUrl = 'https://backend.controlease.com.br/metas';
const dividasDeGastos = ref([]);
const dividasDeCartao = ref([]);
const isFetchingDividas = ref(false);

const isIconDropdownOpen = ref(false);
const iconDropdownContainer = ref(null);

const iconesDisponiveis = [
  { value: 'savings', label: 'Economias / Poupança' }, { value: 'account_balance', label: 'Reserva de Emergência' },
  { value: 'home', label: 'Casa Própria' }, { value: 'directions_car', label: 'Carro Novo' },
  { value: 'flight_takeoff', label: 'Viagem' }, { value: 'school', label: 'Educação / Estudos' },
  { value: 'work', label: 'Carreira / Negócio' }, { value: 'celebration', label: 'Evento / Festa' },
  { value: 'devices', label: 'Eletrônicos / Gadgets' }, { value: 'payments', label: 'Quitar Dívidas' },
  { value: 'add_shopping_cart', label: 'Grande Compra' }, { value: 'flag', label: 'Outro Objetivo' },
];

const authConfig = () => {
  const token = localStorage.getItem('token');
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
};

const formatCurrency = (v) => typeof v === 'number'
  ? v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
  : 'R$ 0,00';

const selectedIconLabel = computed(() => iconesDisponiveis.find(i => i.value === meta.value.icon)?.label || 'Selecione um ícone');

const nomeMesAtual = computed(() => {
  const nome = new Date().toLocaleString('pt-BR', { month: 'long' });
  return nome.charAt(0).toUpperCase() + nome.slice(1);
});

const selectIcon = (v) => {
  meta.value.icon = v;
  isIconDropdownOpen.value = false;
};

const handleClickOutside = (e) => {
  if (iconDropdownContainer.value && !iconDropdownContainer.value.contains(e.target)) {
    isIconDropdownOpen.value = false;
  }
};

const fetchDividas = async () => {
  if (dividasDeGastos.value.length && dividasDeCartao.value.length) return;
  try {
    const [gastos, cartao] = await Promise.all([
      axios.get('https://backend.controlease.com.br/gastos', authConfig()),
      axios.get('https://backend.controlease.com.br/gastos-cartao', authConfig())
    ]);
    dividasDeGastos.value = (gastos.data || []).map(g => ({ ...g, type: 'gasto' }));
    dividasDeCartao.value = (cartao.data || []).map(g => ({ ...g, type: 'cartao' }));
  } catch (e) { console.error('Não foi possível buscar as dívidas.', e); }
};

const calcularEdefinirMetaDividasAtuais = async () => {
  isFetchingDividas.value = true;
  await fetchDividas();
  const hoje = new Date();
  const chave = `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}`;
  const todas = [...dividasDeGastos.value, ...dividasDeCartao.value];
  const total = todas
    .filter(d => d.status === 'PENDENTE' && d.dataPagamento?.startsWith(chave))
    .reduce((s, d) => s + (d.type === 'cartao' ? (d.valorParcela || 0) : (d.valor || 0)), 0);
  meta.value.targetAmount = total > 0 ? total : 1;
  isFetchingDividas.value = false;
};

watch(() => meta.value.icon, (n) => {
  if (n === 'payments') {
    calcularEdefinirMetaDividasAtuais();
  }
}, { immediate: true }); 

watch(isIconDropdownOpen, (o) => {
  o ? document.addEventListener('click', handleClickOutside) : document.removeEventListener('click', handleClickOutside);
});

const fetchMeta = async () => {
  if (!isEditing.value) return;
  try {
    const { data } = await axios.get(`${apiUrl}/${route.params.id}`, authConfig());
    meta.value = data;
  } catch {
    alert('Erro ao carregar meta.');
    router.push('/user/metas');
  }
};

const handleSubmit = async () => {
  if (isSubmitting.value) return;
  isSubmitting.value = true;
  try {
    if (meta.value.targetAmount <= 0) {
      alert('O valor alvo deve ser maior que zero.');
      isSubmitting.value = false;
      return;
    }
    if (isEditing.value) {
      await axios.put(`${apiUrl}/${meta.value.id}`, meta.value, authConfig());
    } else {
      await axios.post(apiUrl, meta.value, authConfig());
    }
    router.push('/user/metas');
  } catch (e) {
    console.error('Erro ao salvar meta:', e);
    alert('Erro ao salvar meta. Verifique os dados e tente novamente.');
  } finally {
    isSubmitting.value = false;
  }
};

const goBack = () => {
  router.push('/user/metas');
};

onMounted(() => {
  if (isEditing.value) {
    fetchMeta();
  } else {
     meta.value = { id: null, title: '', icon: 'savings', currentAmount: 0, targetAmount: 1000 }; 
     if (meta.value.icon === 'payments') calcularEdefinirMetaDividasAtuais();
  }
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<style scoped>
.panel { @apply bg-white/70 dark:bg-brand-800/60 backdrop-blur-lg ring-1 ring-black/5 dark:ring-white/10 rounded-2xl shadow-xl shadow-slate-200/30 dark:shadow-black/20; }
.btn { @apply inline-flex items-center justify-center font-semibold py-2.5 px-5 rounded-xl transition-all duration-200 text-sm active:scale-95 disabled:opacity-60; }
.btn-primary { @apply btn bg-brand-600 hover:bg-brand-700 text-white shadow-lg shadow-brand-500/20; }
.btn-secondary { @apply btn bg-white/70 dark:bg-slate-800/50 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 ring-1 ring-black/5 dark:ring-white/10 shadow-sm; }
.input { @apply w-full px-3.5 py-2.5 text-sm rounded-xl bg-white dark:bg-slate-800/50 border border-slate-300 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500; }
.label { @apply block text-sm font-medium text-slate-700 dark:text-slate-300; }
.animate-spin { animation: spin 1s linear infinite; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
.dropdown-enter-active, .dropdown-leave-active { transition: opacity .12s ease, transform .12s ease; }
.dropdown-enter-from, .dropdown-leave-to { opacity: 0; transform: translateY(-4px); }
</style>